import { Badge } from '@/components/common/badge'
import { Note } from '@/components/common/note'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/common/table'
import { TabsContent } from '@/components/common/tabs'
import { useConnectWalletModalContext } from '@/components/connect/connectModal'
import { TxSteps } from '@/components/opportunity/sidePanel/txSteps'
import type { TabConfig } from '@/components/opportunity/useTabsConfig'
import { useTransaction } from '@/hooks/useTransaction'
import { useActionsQuery } from '@/queries/actions.query'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { StepData } from '@/types/transactionsContext'
import type { Vault } from '@/types/vault'
import { fromUnixTimestamp } from '@vaultsfyi/common/utils'
import { formatDate, formatTokenValue } from '@vaultsfyi/common/utils'
import type { Address } from 'viem'
import type { MellowSpecificData } from './mellowDepositTab'

interface MellowPendingRedeemTabProps {
  account: Address | undefined
  vault: Vault
  stepData: StepData
  tabConfig: TabConfig
  transactionsContext: TransactionsContext
  setTransactionFinished: ({ txHash, amount }: { txHash: string | undefined; amount: string }) => void
  txState: 'idle' | 'loading' | 'success'
}

export function MellowPendingRedeemTab({
  account,
  vault,
  stepData,
  transactionsContext,
  setTransactionFinished,
  txState,
}: MellowPendingRedeemTabProps) {
  const { setOpenModal: setOpenWalletModal } = useConnectWalletModalContext()
  const { network, lpToken } = vault

  const mellowData = transactionsContext?.vaultSpecificData as MellowSpecificData | undefined
  const requestData = mellowData?.requestData

  const redeemRequests = requestData?.redeemRequests || []
  const claimableRedeem = BigInt(requestData?.claimableRedeem ?? 0)
  const isClaimable = claimableRedeem > 0n

  const { data: actions, isFetching } = useActionsQuery({
    address: account,
    action: 'claim-redeem',
    amount: '0',
    vault,
    all: true,
    ignoreAmount: true,
  })

  const { executeTx, currentActionIndex } = useTransaction(network.name, actions, '0', setTransactionFinished)

  const onClaim = async () => {
    if (!account) {
      setOpenWalletModal('connect')
      return
    }
    await executeTx()
  }

  if (!requestData || !lpToken) {
    return (
      <TabsContent value={stepData.name} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
        <p className="text-center text-xs">Loading...</p>
      </TabsContent>
    )
  }

  const isSubmitDisabled = !isClaimable
  const isSubmitLoading = isFetching || txState === 'loading'

  return (
    <TabsContent value={stepData.name} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
      {redeemRequests.length === 0 ? (
        <p className="text-center text-sm">No pending requests.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow variant="header">
              <TableHead>Date</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {redeemRequests.map((req, index) => (
              <TableRow key={`${req.timestamp}-${index}`}>
                <TableCell>
                  {formatDate({
                    timestamp: fromUnixTimestamp(req.timestamp),
                    showYear: true,
                    showTime: true,
                  })}
                </TableCell>
                <TableCell>
                  {formatTokenValue(
                    { value: req.lpTokenAmount, decimals: lpToken.decimals },
                    { minimumFractionDigits: 2 }
                  )}{' '}
                  {lpToken.symbol}
                </TableCell>
                <TableCell>
                  {req.isClaimable ? <Badge color="green">Ready</Badge> : <Badge color="orange">Pending</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {isClaimable && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            onClaim()
          }}
          className="flex flex-col gap-3"
        >
          <Note color="green">
            <p>
              You can now redeem{' '}
              <span className="font-bold">
                {formatTokenValue(
                  { value: claimableRedeem.toString(), decimals: lpToken.decimals },
                  { minimumFractionDigits: 2 }
                )}{' '}
                {lpToken.symbol}
              </span>
            </p>
          </Note>

          <TxSteps
            account={account}
            stepData={stepData}
            activeStep={currentActionIndex}
            loading={isSubmitLoading}
            disabled={isSubmitDisabled}
            label={`Redeem ${formatTokenValue(
              { value: claimableRedeem.toString(), decimals: lpToken.decimals },
              { minimumFractionDigits: 2 }
            )} ${lpToken.symbol}`}
            network={network.name}
            success={txState === 'success'}
          />
        </form>
      )}

      {!isClaimable && redeemRequests.length > 0 && (
        <div className="text-center text-gray-500 text-sm">
          Typically, it takes up to 3 days to finalize the withdrawal request. Check back later to claim.
        </div>
      )}
    </TabsContent>
  )
}
